import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { CONFIG, profanityError } from '@h2h/shared';
import { AppShell, Content } from '../../components/ui/AppShell';
import { Placeholder } from '../../components/ui/Placeholder';
import { LeagueMark, MiniLeagueMark } from '../../components/ui/bits';
import { ArrowRight, Check, ChevronLeft, Clock, Users } from '../../components/ui/icons';
import { useHaptics } from '../../hooks/useHaptics';
import { useAuth } from '../../context/AuthContext';
import { useCreateMiniLeague, useLeagues, useMyMemberships } from '../../hooks/useQueries';
import { identityFor } from '../../data/leagues';
import { PAGE_SLIDE } from '../../lib/motion';
import { ColorPicker, IconPicker, ModalNav } from './parts';
import { cn } from '../../lib/cn';
import { MiniLeagueExplainer } from './MiniLeagueExplainer';
import type { MiniLeagueNavState } from './miniLeagueHelpers';

export default function Create() {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as MiniLeagueNavState | null;
  const [params] = useSearchParams();
  const presetLeagueId = params.get('leagueId');
  const { impact } = useHaptics();
  const { uid } = useAuth();
  const { data: memberships } = useMyMemberships(uid);
  const { data: leagues } = useLeagues();
  const create = useCreateMiniLeague(uid);

  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState(CONFIG.MINI_LEAGUE.ICONS[0]);
  const [color, setColor] = useState(CONFIG.MINI_LEAGUE.COLORS[0]);
  const [leagueId, setLeagueId] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // Focus the name field only *after* the push slide settles. Focusing while the
  // screen is still animating in makes iOS open the keyboard and scroll-into-view
  // mid-transition (the screen is parked off-screen at that instant), which fights
  // the GPU slide on the main thread — the "weird and laggy" push. Waiting for the
  // slide to finish keeps the transition pure compositor work, then the keyboard
  // rises cleanly. (Plain `autoFocus` is what caused the jank.)
  useEffect(() => {
    if (step !== 1) return;
    const t = window.setTimeout(
      () => nameRef.current?.focus(),
      (PAGE_SLIDE.duration ?? 0.34) * 1000 + 40
    );
    return () => window.clearTimeout(t);
  }, [step]);

  const joinedLeagueIds = (memberships ?? []).map((m) => m.leagueId);

  useEffect(() => {
    if (!memberships || !presetLeagueId) return;
    if (joinedLeagueIds.includes(presetLeagueId)) setLeagueId(presetLeagueId);
  }, [memberships, presetLeagueId, joinedLeagueIds]);

  const selectedLeagueId = leagueId ?? joinedLeagueIds[0] ?? null;

  const leagueOf = (id: string) => leagues?.find((l) => l.leagueId === id);
  const nameOf = (id: string) => leagueOf(id)?.name ?? identityFor(id)?.name ?? 'League';
  const codeOf = (id: string) => leagueOf(id)?.shortCode ?? identityFor(id)?.shortCode ?? 'PL';
  const countryOf = (id: string) => leagueOf(id)?.country ?? identityFor(id)?.country ?? '';
  const seasonOf = (id: string) => leagueOf(id)?.season;

  const noMembership = !!memberships && memberships.length === 0;
  const nameError = profanityError(name, 'league name');

  const submit = () => {
    if (!selectedLeagueId || !name.trim() || nameError || create.isPending) return;
    impact('success');
    create.mutate(
      { leagueId: selectedLeagueId, name: name.trim(), icon, color, settings: { roundReminders: false } },
      { onSuccess: (res) => navigate(`/leagues/${res.miniLeague.miniLeagueId}/share`, { replace: true }) }
    );
  };

  if (noMembership) {
    return (
      <AppShell>
        <Content>
          <ModalNav title="New league" onCancel={() => navigate('/leagues', { state: navState })} />
          <Placeholder
            Icon={Users}
            title="Join a league first"
            body="A mini league sits on top of a base league you play. Join one, then come back to start a private table."
          />
          <button
            type="button"
            onClick={() => navigate('/')}
            className="tap mt-4 w-full rounded-[16px] bg-night py-4 text-center text-[15px] font-extrabold text-white"
          >
            Browse leagues
          </button>
        </Content>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Content>
        <div className="contents md:mx-auto md:block md:w-full md:max-w-lg">
        {step === 1 ? (
          <ModalNav title="New league" onCancel={() => navigate('/leagues', { state: navState })} />
        ) : (
          <div className="flex items-center justify-between pt-2 md:pt-5">
            <button
              type="button"
              onClick={() => {
                impact('light');
                setStep(1);
              }}
              className="tap -ml-1 flex items-center gap-[2px] text-ink"
            >
              <ChevronLeft width={22} height={22} strokeWidth={2.4} />
              <span className="text-[16px] font-semibold tracking-snug">Identity</span>
            </button>
            <span className="pr-1.5 text-[14px] font-bold tracking-snug text-ink">New league</span>
          </div>
        )}

        {step === 1 ? (
          <div className="flex flex-1 flex-col justify-center pb-[4vh]">
            {/* live preview */}
            <div className="flex flex-col items-center pt-5">
              <MiniLeagueMark icon={icon} color={color} size={96} />
              <div className="mt-3.5 text-[19px] font-extrabold tracking-tight text-ink">
                {name.trim() || 'Your league'}
              </div>
              <div className="mt-0.5 text-[12px] font-semibold text-ink2">Live preview</div>
            </div>

            {/* name */}
            <div className="pt-6">
              <label className="text-[12px] font-bold uppercase tracking-wide text-ink2">League name</label>
              <div className="mt-[9px] flex items-center gap-1 rounded-[16px] border-[1.5px] border-ink bg-fill px-4 py-[14px]">
                <input
                  ref={nameRef}
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, CONFIG.MINI_LEAGUE.NAME_MAX_LENGTH))}
                  placeholder="The Lads"
                  className="flex-1 bg-transparent text-[16px] font-bold tracking-snug text-ink outline-none placeholder:text-ink3"
                />
                <span className="num text-[12px] font-semibold text-ink3">
                  {name.length}/{CONFIG.MINI_LEAGUE.NAME_MAX_LENGTH}
                </span>
              </div>
              {nameError && <p className="mt-2 text-[13px] font-semibold text-danger">{nameError}</p>}
            </div>

            {/* icon */}
            <div className="pt-6">
              <label className="text-[12px] font-bold uppercase tracking-wide text-ink2">Pick an icon</label>
              <IconPicker value={icon} onChange={setIcon} />
            </div>

            {/* colour */}
            <div className="pt-6">
              <label className="text-[12px] font-bold uppercase tracking-wide text-ink2">Pick a colour</label>
              <ColorPicker value={color} onChange={setColor} />
            </div>

            <StepDots step={1} />
            <button
              type="button"
              disabled={!name.trim() || !!nameError}
              onClick={() => {
                impact('light');
                setStep(2);
              }}
              className="tap mt-3 flex w-full items-center justify-center gap-2 rounded-[17px] bg-gradient-to-br from-accent to-accent-deep py-[17px] shadow-featured disabled:opacity-50"
            >
              <span className="text-[16px] font-extrabold tracking-snug text-white">Next</span>
              <ArrowRight width={17} height={17} strokeWidth={2.8} className="text-white" />
            </button>
          </div>
        ) : (
          <>
            <div className="pt-4">
              <h1 className="text-[25px] font-extrabold leading-[1.08] tracking-title text-ink">
                What are you
                <br />
                playing for?
              </h1>
              <p className="mt-2 text-[13px] font-medium text-ink2">Pick the competition your table follows.</p>
            </div>

            {/* competition list */}
            <div className="flex flex-col gap-[9px] pt-[18px]">
              {joinedLeagueIds.map((id) => {
                const active = id === selectedLeagueId;
                const season = seasonOf(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      impact('selection');
                      setLeagueId(id);
                    }}
                    className={cn(
                      'tap flex items-center gap-[13px] rounded-[18px] bg-card p-[13px_14px] text-left',
                      active
                        ? 'border-[1.5px] border-ink shadow-[0_10px_24px_-16px_rgba(0,0,0,0.25)]'
                        : 'border border-black/8'
                    )}
                  >
                    <LeagueMark leagueId={id} country={countryOf(id)} code={codeOf(id)} size={42} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-extrabold tracking-snug text-ink">{nameOf(id)}</div>
                      <div className="truncate text-[12px] font-medium text-ink2">
                        {countryOf(id)}
                        {season ? ` · ${season}/${(season + 1) % 100}` : ''}
                      </div>
                    </div>
                    {active ? (
                      <span className="grid h-6 w-6 place-items-center rounded-full bg-accent">
                        <Check width={13} height={13} strokeWidth={3.5} className="text-white" />
                      </span>
                    ) : (
                      <span className="h-[22px] w-[22px] rounded-full border-2 border-ink4" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="pt-4">
              <MiniLeagueExplainer leagueName={selectedLeagueId ? nameOf(selectedLeagueId) : undefined} />
            </div>

            <div className="pt-5">
              <div className="flex items-center gap-1.5 rounded-[14px] bg-fill px-3.5 py-3 text-[12px] font-medium text-ink2">
                <Clock width={13} height={13} strokeWidth={2} className="shrink-0 text-ink3" />
                Runs for the full season — everyone keeps playing as normal.
              </div>
            </div>

            {create.isError && (
              <p className="mt-4 text-center text-[12px] font-semibold text-danger">
                Couldn’t create the league. Try again.
              </p>
            )}

            <StepDots step={2} />
            <button
              type="button"
              disabled={create.isPending}
              onClick={submit}
              className="tap mt-3 w-full rounded-[17px] bg-gradient-to-br from-accent to-accent-deep py-[17px] text-center text-[16px] font-extrabold tracking-snug text-white shadow-featured disabled:opacity-60"
            >
              {create.isPending ? 'Creating…' : 'Create league'}
            </button>
          </>
        )}
        </div>
      </Content>
    </AppShell>
  );
}

function StepDots({ step }: { step: 1 | 2 }) {
  return (
    <div className="mt-8 flex justify-center gap-1.5">
      <span className={cn('h-[6px] rounded-full', step === 1 ? 'w-[26px] bg-ink' : 'w-2 bg-ink4')} />
      <span className={cn('h-[6px] rounded-full', step === 2 ? 'w-[26px] bg-ink' : 'w-2 bg-ink4')} />
    </div>
  );
}
